import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { UserService } from '../../shared/services/user.service';

interface OneTrustConfig {
  scriptSrc: string;
  domainId: string;
}

interface AdobeLaunchConfig {
  scriptSrc: string;
}
@Injectable({
  providedIn: 'root'
})
export class InitService {
  constructor(private router: Router, private userService: UserService) { }

  initializeApp(): Promise<any> {
    return Promise.all([
      this.loadOneTrustScript(),
      this.loadAdobeScript()
    ]).then(() => {
      this.setupPageTracking();
    });
  }

  private loadOneTrustScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const oneTrustConfig: OneTrustConfig | undefined = (window as any).__env?.oneTrust;

        if (oneTrustConfig) {
          const script = document.createElement('script');
          script.src = oneTrustConfig.scriptSrc;
          script.type = "text/javascript";
          script.charset = "UTF-8";
          script.setAttribute('data-domain-script', oneTrustConfig.domainId);
          script.async = true;

          const wrapperScript = document.createElement('script');
          let oneTrustWrapperCode = `function OptanonWrapper() {
            document.getElementById('close-pc-btn-handler').addEventListener("click", function () {
              if (Optanon.IsAlertBoxClosed()) {
                removeOneTrustDarkFilter();
              }
            });

            function removeOneTrustDarkFilter() {
              var darkFilter = document.getElementsByClassName('onetrust-pc-dark-filter');
              for (var i = 0; i < darkFilter.length; i++) {
                darkFilter[i].style.display = "none";
              }
            }
          }`
          const wrapperContent = new Blob(
            [oneTrustWrapperCode],
            { type: 'application/javascript' }
          );
          const wrapperUrl = URL.createObjectURL(wrapperContent);
          wrapperScript.src = wrapperUrl;
          wrapperScript.type = "text/javascript";

          document.head.appendChild(script);
          document.head.appendChild(wrapperScript);

          wrapperScript.onload = () => {
            URL.revokeObjectURL(wrapperUrl);
          };

          script.onload = () => {
            resolve();
          };

          script.onerror = (error) => {
            console.warn('Failed to load OneTrust script:', error);
            // We resolve anyway to not block app initialization
            resolve();
          };
        } else {
          console.warn('OneTrust configuration not found in environment settings');
          resolve();
        }
      } catch (error) {
        console.warn('Error in OneTrust initialization:', error);
        // We resolve anyway to not block app initialization
        resolve();
      }
    });
  }

  private loadAdobeScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const adobeConfig: AdobeLaunchConfig | undefined = (window as any).__env?.adobeLaunch;

        if (adobeConfig) {
          this.initializeAnalyticsDataLayer();
          const script = document.createElement('script');
          script.src = adobeConfig.scriptSrc;
          script.type = "text/javascript";
          script.async = true;

          document.head.appendChild(script);

          script.onload = () => {           
            resolve();
          };

          script.onerror = (error) => {
            console.warn('Failed to load Adobe Launch script:', error);
            resolve();
          };
        } else {
          console.warn('Adobe Launch configuration not found in environment settings');
          resolve();
        }
      } catch (error) {
        console.warn('Error in Adobe Launch initialization:', error);
        resolve();
      }
    });
  }

  private initializeAnalyticsDataLayer(): void {
    try {
      const dataLayerScript = document.createElement('script');
      const dataLayerContent = new Blob([
        `var analyticsDataLayer = {
          'pageInfo': {
            'pageName': "${this.getPageName()}",
            'userID': "${this.getUserId()}", 
            'applicationName': "${this.getApplicationName()}"
          }
        };`
      ], { type: 'application/javascript' });

      const dataLayerUrl = URL.createObjectURL(dataLayerContent);
      dataLayerScript.src = dataLayerUrl;
      dataLayerScript.type = "text/javascript";

      document.head.appendChild(dataLayerScript);

      dataLayerScript.onload = () => {
        URL.revokeObjectURL(dataLayerUrl);
      };

      (window as any).updateAnalyticsDataLayer = this.updateAnalyticsDataLayer.bind(this);
    } catch (error) {
      console.warn('Error initializing analytics data layer:', error);
    }
  }

  private setupPageTracking(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateAnalyticsDataLayer();
      this.trackPageView();
    });
  }

  private updateAnalyticsDataLayer(): void {
    if ((window as any).analyticsDataLayer) {
      (window as any).analyticsDataLayer.pageInfo.pageName = this.getPageName();
      (window as any).analyticsDataLayer.pageInfo.userID = this.getUserId();
      (window as any).analyticsDataLayer.pageInfo.applicationName = this.getApplicationName();
    }
  }

  private trackPageView(): void {
    if ((window as any)._satellite) {
      (window as any)._satellite.track("pageView");
    } else {
      console.warn('_satellite not available for tracking');
    }
  }

  private getUserId(): string {
    const userDetails = this.userService.getUserDetailsAfterLogin();

    if (userDetails) {
      if (Array.isArray(userDetails) && userDetails.length > 0) {
        return userDetails[0].userName || 'userNotAuthenticated';
      } else if (userDetails.userName) {
        return userDetails.userName;
      }
    }
    return 'userNotAuthenticated';
  }

  private getApplicationName(): string {
    return "ECODE INTERNAL";
  }

  private getPageName(): string {
    const currentUrl = this.router.url;
    return currentUrl ? currentUrl.substring(1) || "home" : "home";
  }
}